/**
 * LoginPage.jsx
 * Dedicated sign-in page at /login.
 *
 * Responsibilities:
 *   - Shows the "Continue with Google" button
 *   - Explains what QuitPact stores and why
 *   - Redirects the user after successful sign-in:
 *       * location.state.from (if present, e.g. they were sent here from a
 *         protected route)
 *       * otherwise "/" (home)
 *   - If the user is already signed in, redirects away immediately.
 *
 * Team: TA37 - The Coders' Assembly
 */

import React, { useCallback, useEffect, useState } from "react";
import { Hand } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import GoogleSignInButton from "../components/common/GoogleSignInButton";
import { useAuth } from "../contexts/AuthContext";
import "./LoginPage.css";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signInWithGoogle } = useAuth();

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Where should we send the user after a successful sign-in?
  const redirectTo = location.state?.from || "/dashboard";

  // Already signed in? Bounce away.
  useEffect(() => {
    if (!loading && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [loading, user, redirectTo, navigate]);

  const handleGoogleSuccess = useCallback(
    async (idToken) => {
      setError("");
      setSubmitting(true);
      try {
        await signInWithGoogle(idToken);
        navigate(redirectTo, { replace: true });
      } catch (err) {
        console.error(err);
        setError(err.message || "Sign-in failed. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
    [signInWithGoogle, navigate, redirectTo],
  );

  const handleGoogleError = useCallback((message) => {
    setError(message);
  }, []);

  if (loading) {
    return (
      <main className="login-page" aria-label="Sign in">
        <div className="login-page__container">
          <p className="login-page__status">Checking your sign-in status...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="login-page" aria-label="Sign in">
      <div className="login-page__container">
        <header className="login-page__header">
          <div className="login-page__icon" aria-hidden="true">
            <Hand />
          </div>
          <h1 className="login-page__heading">Welcome to QuitPact</h1>
          <p className="login-page__subheading">
            Sign in to save your check-in results, track your streak, and see
            how your risk changes over time.
          </p>
        </header>

        <section className="login-page__card" aria-label="Sign in with Google">
          <GoogleSignInButton
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
          />

          {submitting && (
            <p className="login-page__status" role="status">
              Signing you in...
            </p>
          )}

          {error && (
            <p className="login-page__error" role="alert">
              {error}
            </p>
          )}
        </section>

        <section className="login-page__disclosure" aria-label="What we store">
          <h2 className="login-page__disclosure-heading">
            What QuitPact stores when you sign in
          </h2>
          <ul className="login-page__disclosure-list">
            <li>
              Your Google email address, display name, and profile picture.
            </li>
            <li>The check-in results you choose to save.</li>
            <li>Your progress streak and history.</li>
          </ul>
          <p className="login-page__disclosure-note">
            We never store your Google password. You can sign out at any time
            from the navigation bar. QuitPact is for informational purposes only
            and does not constitute medical advice.
          </p>
        </section>

        <div className="login-page__guest-note">
          <p>
            You can still use the check-in without signing in, but your results
            will not be saved.
          </p>
        </div>
      </div>
    </main>
  );
};

export default LoginPage;
